import { FC } from 'react';
import { Card, CardBody } from 'reactstrap';
import JobOrderBlock, { JobOrderBlockData } from './JobOrderBlock';

export interface CompanyGroupData {
  companyId: string;
  companyName: string | null | undefined;
  jobOrders: JobOrderBlockData[];
}

interface Props {
  data: CompanyGroupData;
  isExpanded: boolean;
  onToggleExpand: (companyId: string) => void;
  // Pass-through to JobOrderBlock
  expandedJobOrders: Record<string, boolean>;
  onToggleJobOrder: (jobOrderId: string) => void;
  intakeJobOrderId: string | null;
  onToggleIntake: (jobOrderId: string) => void;
  onIntakeUploaded: (jobOrderId: string, batchId: string) => void;
  selectionJoId: string | null;
  selectedIds: string[];
  onToggleSelect: (jobOrderId: string, pipelineEntryId: string) => void;
  onSelectAll: (jobOrderId: string, pipelineEntryIds: string[], checked: boolean) => void;
  onClearSelection: () => void;
  onActionDone: (actedIds: string[]) => void;
  onOpenTrail: (pipelineEntryId: string, candidateName: string | null | undefined, candidateTitle: string | null | undefined) => void;
  onRescored: () => void;
  isFiltering: boolean;
}

const CompanyGroup: FC<Props> = ({
  data,
  isExpanded,
  onToggleExpand,
  expandedJobOrders,
  onToggleJobOrder,
  intakeJobOrderId,
  onToggleIntake,
  onIntakeUploaded,
  selectionJoId,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onActionDone,
  onOpenTrail,
  onRescored,
  isFiltering,
}) => {
  const jobOrderCount = data.jobOrders.length;

  // When filtering, hide if all job orders have 0 matches
  const visibleJobOrders = isFiltering
    ? data.jobOrders.filter((jo) => jo.matchCount > 0)
    : data.jobOrders;

  if (isFiltering && visibleJobOrders.length === 0) {
    return null;
  }

  return (
    <Card className="ai-console__company-group mb-3">
      <div
        className="ai-console__company-header"
        role="button"
        tabIndex={0}
        onClick={() => onToggleExpand(data.companyId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand(data.companyId);
          }
        }}
      >
        <span
          className={`ai-console__caret ${isExpanded ? 'ai-console__caret--open' : ''}`}
        >
          &#9654;
        </span>
        <span className="ai-console__company-name">
          {data.companyName ?? 'Unknown Company'}
        </span>
        <span className="ai-console__company-meta text-muted ml-2">
          {jobOrderCount} job order{jobOrderCount !== 1 ? 's' : ''}
        </span>
      </div>

      {isExpanded && (
        <CardBody className="ai-console__company-body">
          {data.jobOrders.map((jo) => (
            <JobOrderBlock
              key={jo.jobOrderId}
              companyId={data.companyId}
              data={jo}
              isExpanded={!!expandedJobOrders[jo.jobOrderId]}
              onToggleExpand={onToggleJobOrder}
              intakeOpen={intakeJobOrderId === jo.jobOrderId}
              onToggleIntake={onToggleIntake}
              onIntakeUploaded={onIntakeUploaded}
              selectionJoId={selectionJoId}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              onSelectAll={onSelectAll}
              onClearSelection={onClearSelection}
              onActionDone={onActionDone}
              onOpenTrail={onOpenTrail}
              onRescored={onRescored}
              isFiltering={isFiltering}
            />
          ))}
        </CardBody>
      )}
    </Card>
  );
};

export default CompanyGroup;
